import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { images } from '../../data/content';
import { CheckCircle, CalendarCheck } from 'lucide-react';

const AboutSection = () => {
  const { t, isRTL } = useLanguage();
  const [aboutCms, setAboutCms] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetch(`${backendUrl}/api/cms/about`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAboutCms(data); })
      .catch(() => {});
  }, [backendUrl]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const portrait = aboutCms?.image_url || images.about.portrait;
  const displayTagline = isRTL ? (aboutCms?.tagline_ar || t.about.tagline) : (aboutCms?.tagline || t.about.tagline);
  const displayTitle = isRTL ? (aboutCms?.title_ar || t.about.headline) : (aboutCms?.title || t.about.headline);
  const chips = (isRTL ? aboutCms?.tags_ar : aboutCms?.tags) && (isRTL ? aboutCms.tags_ar : aboutCms.tags).length > 0
    ? (isRTL ? aboutCms.tags_ar : aboutCms.tags)
    : (t.about.role || '').split(' · ').filter(Boolean);
  const creds = (isRTL ? aboutCms?.credentials_ar : aboutCms?.credentials) && (isRTL ? aboutCms.credentials_ar : aboutCms.credentials).length > 0
    ? (isRTL ? aboutCms.credentials_ar : aboutCms.credentials)
    : t.about.credentials;
  const bio = (isRTL ? aboutCms?.bio_ar : aboutCms?.bio) && (isRTL ? aboutCms.bio_ar : aboutCms.bio).length > 0
    ? (isRTL ? aboutCms.bio_ar : aboutCms.bio)
    : t.about.story;

  return (
    <section id="about" className="section-padding bg-charcoal">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-20 items-center">

          {/* ── Image Side ── */}
          <div className={`${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
            <div className="relative max-w-md mx-auto lg:max-w-none">
              <div className="aspect-[3/4] overflow-hidden rounded-2xl">
                <img
                  src={portrait}
                  alt={displayTitle}
                  className="w-full h-full object-cover rounded-2xl"
                  loading="lazy"
                />
              </div>
              {/* Decorative border - hide on small mobile */}
              <div className="hidden sm:block absolute -bottom-4 -right-4 w-full h-full border border-gold/30 -z-10 rounded-2xl" />
            </div>
          </div>

          {/* ── Content Side ── */}
          <div className={`${isRTL ? 'lg:order-1' : 'lg:order-2'} mt-6 lg:mt-0`}>

            {/* Zone 1 — Identity */}
            <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
              {displayTagline}
            </p>

            <h2 className="font-script text-3xl sm:text-4xl lg:text-5xl text-gold mb-4">
              {displayTitle}
            </h2>

            {/* Role chips — scannable at a glance */}
            <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
              {chips.map((chip, i) => (
                <span
                  key={i}
                  className="inline-block text-xs text-gold border border-gold/35 px-2.5 py-1 rounded-full bg-gold/5 tracking-wide"
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="gold-divider !mx-0 !ml-0 mb-6 sm:mb-8" />

            {/* Zone 2 — Credentials */}
            <div className="mb-7 sm:mb-9">
              <p className="text-gold/70 text-xs uppercase tracking-wider mb-3 sm:mb-4">
                {isRTL ? 'التدريب والمؤهلات' : 'Training & Credentials'}
              </p>
              <div className="space-y-2 sm:space-y-2.5">
                {creds.map((credential, index) => (
                  <div key={index} className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle size={13} className="text-gold flex-shrink-0" />
                    <span className="text-cream-300 text-xs sm:text-sm leading-snug">{credential}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Zone 3 — Story */}
            <div className="border-t border-gold/12 pt-6 sm:pt-8 mb-8 sm:mb-10">
              <div className="space-y-4 sm:space-y-5">
                <p className="text-cream text-sm sm:text-base leading-relaxed font-light">
                  {bio[0]}
                </p>
                {bio[1] && (
                  <div className="bg-gold/[0.06] border border-gold/20 rounded-xl px-4 py-3 sm:px-5 sm:py-4">
                    <p className="text-cream-200 text-sm sm:text-base leading-relaxed italic">
                      {bio[1]}
                    </p>
                  </div>
                )}
                {bio[2] && (
                  <p className="text-cream-300 text-sm sm:text-base leading-relaxed">
                    {bio[2]}
                  </p>
                )}
              </div>
            </div>

            {/* Zone 4 — CTA */}
            <button
              onClick={() => scrollToSection('connect')}
              className="btn-gold inline-flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start"
            >
              <CalendarCheck size={16} className="text-charcoal" />
              <span>{t.about.cta}</span>
            </button>

          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
